import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useT } from '../../i18n';
import { AmountField } from './OpenPosition';
import { getTokenIcon, num, toUnits, toInput, formatUsd, formatAmount, sameAddress } from './cdpHelpers';

const WITHDRAW_STEPS = [25, 50, 100];

const PoolCard = ({ synth, market, account, actions }) => {
  const t = useT();
  const [input, setInput] = useState('');
  const stats = synth.poolStats;
  const mine = account?.pools[synth.ticker];
  const price = synth.price ? num(synth.price) : null;
  const totalValue = stats.totalValue ? num(stats.totalValue) : null;
  const walletSynth = account ? num(account.balances[synth.token.toLowerCase()]) : 0;
  const sharePct = mine && !stats.totalShares.isZero() ? num(mine.shares.mul(10 ** 6).div(stats.totalShares), 4) : 0;
  const units = toUnits(input, 18);

  const symbolOf = (token) => market.collaterals.find((c) => sameAddress(c.address, token)) || { symbol: '?', decimals: 18 };

  const deposit = async () => {
    await actions.poolDeposit(synth.pool, synth.token, units);
    setInput('');
  };
  const withdraw = (pct) => actions.poolWithdraw(synth.pool, pct === 100 ? mine.shares : mine.shares.mul(pct).div(100));

  return (
    <div className="lb-tile p-4 sm:p-5">
      <div className="flex items-center gap-3">
        {getTokenIcon(synth.ticker) ? (
          <img src={getTokenIcon(synth.ticker)} alt={synth.ticker} className="h-10 w-10 rounded-full" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full lb-fill-brand text-xs font-bold text-white">
            {synth.ticker.slice(2, 4)}
          </div>
        )}
        <div>
          <p className="notranslate font-bold text-white">{synth.ticker}</p>
          <p className="text-xs text-white/45">{t('cynths.poolOf', { ticker: synth.ticker })}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="lb-label">{t('cynths.poolValue')}</p>
          <p className="notranslate lb-num text-white">
            {totalValue === null ? 'N/A' : `${formatAmount(totalValue, 6)} ${synth.ticker}`}
          </p>
          <p className="notranslate text-xs text-white/45">{formatUsd(totalValue !== null && price !== null ? totalValue * price : null)}</p>
        </div>
        <div>
          <p className="lb-label">{t('cynths.poolAvailable')}</p>
          <p className="notranslate lb-num text-white">
            {formatAmount(num(stats.synthBalance), 6)} {synth.ticker}
          </p>
        </div>
        {mine && !mine.shares.isZero() && (
          <>
            <div>
              <p className="lb-label">{t('cynths.yourShare')}</p>
              <p className="notranslate lb-num text-white">{sharePct.toFixed(2)}%</p>
            </div>
            <div>
              <p className="lb-label">{t('cynths.yourClaim')}</p>
              <p className="notranslate lb-num text-white">
                {formatAmount(num(mine.synthOut), 6)} {synth.ticker}
              </p>
              {mine.gains.map((g) => {
                const c = symbolOf(g.token);
                return (
                  <p key={g.token} className="notranslate text-xs text-emerald-400">
                    + {formatAmount(num(g.amount, c.decimals), 6)} {c.symbol}
                  </p>
                );
              })}
            </div>
          </>
        )}
      </div>

      {account && (
        <div className="mt-4 space-y-3 border-t border-white/5 pt-4">
          <AmountField
            label={t('cynths.depositSynth')}
            balanceLabel={`${t('cynths.balance')}: ${formatAmount(walletSynth, 6)}`}
            value={input}
            onChange={setInput}
            onMax={() => setInput(toInput(walletSynth, 8))}
            suffix={synth.ticker}
          />
          <button
            className="lb-btn lb-btn-primary w-full"
            disabled={!units || parseFloat(input) > walletSynth * 1.000001}
            onClick={deposit}
          >
            {t('cynths.deposit')}
          </button>
          {mine && !mine.shares.isZero() && (
            <div className="grid grid-cols-3 gap-2">
              {WITHDRAW_STEPS.map((pct) => (
                <button key={pct} className="lb-btn lb-btn-outline lb-btn-sm" onClick={() => withdraw(pct)}>
                  {t('cynths.withdrawPct', { pct })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StabilityPools = ({ market, account, actions }) => {
  const t = useT();
  const pools = market.synths.filter((s) => s.poolStats && s.pool !== ethers.constants.AddressZero);
  return (
    <div className="space-y-4">
      <p className="text-sm text-white/60">{t('cynths.poolsBlurb')}</p>
      {!account && <p className="text-sm text-amber-300">{t('cynths.connectFirst')}</p>}
      {pools.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {pools.map((s) => (
            <PoolCard key={s.ticker} synth={s} market={market} account={account} actions={actions} />
          ))}
        </div>
      ) : (
        <p className="text-sm text-white/55">{t('cynths.noPools')}</p>
      )}
    </div>
  );
};

export default StabilityPools;
